// The §4.1 / §4.2 "Reserve" control (spec 06 §5 row 29, §2.6).
//
// WHAT THIS PANEL USED TO BE, AND WHY IT WAS BROKEN (fixed 2026-08-20, D-151).
// It asked for a `demand_id` and refused to submit until the field matched a
// uuid regex. Every one of the four user-mintable §2.6 demand kinds names a row
// in another module, so the only way anyone could satisfy that control was to
// invent a uuid — which means a storekeeper could not reserve a bobbin against
// a real customer order at all. A control that cannot succeed is worse than an
// absent one: it reads as "you are holding it wrong".
//
// WHAT IT IS NOW. One kind of hold is placeable from here, and it is the one
// that names nothing by construction: a QUOTE HOLD — stock held while somebody
// quotes. It asks for the words the storekeeper already has ("BuildCo · 200 m
// of 70 mm² · Thursday"), and the server derives the demand id from them, so
// the reference GROUPS like a real demand: type the same reference again and
// the second drum joins the first hold. A quote hold also expires on its own
// (§2.6, 48 h by default), which is what makes it safe to place against words
// rather than a row.
//
// THE OTHER THREE KINDS ARE ABSENT, NOT DISABLED, AND THE PANEL SAYS WHY
// (D-118). A sales-order line, a work-order component and a maintenance part
// are all minted BY their owning module together with the hold — module 04
// already reserves every component of a work order when it is released, and
// nobody should be typing those ids by hand. Modules 09 and 08 are not built.
// So the panel names all three and what will bring them, instead of offering a
// picker over kinds whose ids no one in the building can produce.
//
// A PASTED UUID STILL WORKS. If what the user types IS a uuid, it is sent as
// `demand_id` verbatim rather than as words to hash — the one caller with a
// real id (an integration, a planner reading one off an API response) keeps the
// path it had, and the field's `reserve-demand-id` testid is unchanged so the
// §10 walkthroughs that drive it are untouched. The name is historical; what
// the field asks for is a reference.
//
// MULTI-TARGET (§4.1 "Reserve both", the off-cut combo action): one reference,
// one reservation PER PIECE (§2.6 holds a unit, not a set), created in order
// and reported per piece — a partial success must never read as a clean one,
// because the second piece is the half the customer's length depends on. All
// the pieces share the derived demand, which is the whole reason the reference
// is hashed rather than stored as loose text.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { CheckboxField, Field } from "../../../../components/field";
import { useCreateReservation } from "../hooks/inventory-queries";
import { inventoryErrorMessage, problemExtension } from "../lib/errors";
import { formatMeasure, parseMeasure } from "../lib/measure";

/** The kinds this panel does NOT place, and the module that does. Rendered as
 * words (D-118): a kind whose ids nobody here can produce is absent WITH its
 * reason, never a select option that 422s. */
const OTHER_KINDS = ["work_order_component", "sales_order_line", "maintenance_order_part"] as const;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/** The contract's floor (`demand_ref` min 3) mirrored so the refusal is inline
 * rather than a round-trip. The server is still the authority. */
const REF_MIN = 3;

export interface ReserveTarget {
  readonly productId: string;
  readonly facilityId: string;
  readonly uom: string;
  readonly quantity: string;
  readonly stockUnitId?: string | undefined;
  readonly lotId?: string | undefined;
  readonly label: string;
}

interface PieceOutcome {
  readonly label: string;
  readonly ok: boolean;
  readonly detail: string;
}

export function ReservePanel({
  target,
  targets,
  onClose,
}: {
  target?: ReserveTarget | undefined;
  targets?: readonly ReserveTarget[] | undefined;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const create = useCreateReservation();
  const list: readonly ReserveTarget[] = targets ?? (target !== undefined ? [target] : []);
  const multi = list.length > 1;
  const single = list[0];

  const [demandRef, setDemandRef] = useState("");
  const [quantity, setQuantity] = useState(single?.quantity ?? "");
  const [allowBackorder, setAllowBackorder] = useState(false);
  const [outcomes, setOutcomes] = useState<PieceOutcome[] | null>(null);
  const [busy, setBusy] = useState(false);

  const trimmedRef = demandRef.trim();
  const refTooShort = trimmedRef.length > 0 && trimmedRef.length < REF_MIN;
  const refReady = trimmedRef.length >= REF_MIN;
  const allOk = outcomes?.every((outcome) => outcome.ok) === true;

  async function submit() {
    setBusy(true);
    // A uuid IS an id; anything else is words the server hashes into one.
    const demand = UUID_RE.test(trimmedRef)
      ? { demand_id: trimmedRef }
      : { demand_ref: trimmedRef };
    const results: PieceOutcome[] = [];
    for (const item of list) {
      // Sequential, not parallel: two holds against the same demand are two
      // §2.6 rows, and a race on the one-active-hold-per-unit UNIQUE would
      // surface as a confusing RESERVATION_CONFLICT on the wrong piece.
      try {
        const created = await create.mutateAsync({
          product_id: item.productId,
          facility_id: item.facilityId,
          quantity: multi ? item.quantity : quantity.trim(),
          uom: item.uom,
          demand_type: "quote_hold",
          ...demand,
          allow_backorder: allowBackorder,
          ...(item.stockUnitId !== undefined ? { stock_unit_id: item.stockUnitId } : {}),
          ...(item.lotId !== undefined ? { lot_id: item.lotId } : {}),
        });
        const held = t("inventory.reserve.created", {
          quantity: formatMeasure(created.reservation.quantity, { locale: i18n.language }),
          uom: created.reservation.uom,
        });
        const expires = created.reservation.expires_at;
        results.push({
          label: item.label,
          ok: true,
          // The expiry is READ BACK from the created row, never guessed: the
          // window is a tenant setting (§2.13) this screen does not hold, and
          // "48 hours" printed from the client would be wrong for any tenant
          // that changed it.
          detail:
            expires === null
              ? held
              : `${held} ${t("inventory.reserve.expiresAt", {
                  when: new Date(expires).toLocaleString(i18n.language),
                })}`,
        });
      } catch (error) {
        const available = problemExtension(error, "available_qty_base");
        const message = inventoryErrorMessage(t, error, "inventory.reserve.failed");
        const heldBy = problemExtension(error, "demand_ref");
        const withHolder =
          heldBy === undefined
            ? message
            : `${message} ${t("inventory.reserve.heldFor", { ref: heldBy })}`;
        results.push({
          label: item.label,
          ok: false,
          detail:
            available === undefined
              ? withHolder
              : `${withHolder} ${t("inventory.reserve.available", {
                  quantity: formatMeasure(available, { locale: i18n.language }),
                  uom: item.uom,
                })}`,
        });
      }
    }
    setOutcomes(results);
    setBusy(false);
  }

  if (list.length === 0) return null;

  return (
    <section
      className="inv-panel"
      data-testid="reserve-panel"
      aria-label={t("inventory.reserve.title")}
    >
      <h2>{t("inventory.reserve.title")}</h2>
      <p className="inv-panel-sub">{list.map((item) => item.label).join(" + ")}</p>

      {outcomes !== null ? (
        <>
          <p
            className={allOk ? "inv-success" : "form-error"}
            data-testid="reserve-outcome"
            data-ok={String(allOk)}
            role="status"
          >
            {allOk
              ? t("inventory.reserve.allCreated", { n: outcomes.length })
              : t("inventory.reserve.partial", {
                  ok: outcomes.filter((outcome) => outcome.ok).length,
                  n: outcomes.length,
                })}
          </p>
          <ul className="inv-list">
            {outcomes.map((outcome) => (
              <li
                key={outcome.label}
                data-testid="reserve-outcome-row"
                data-ok={String(outcome.ok)}
                className={outcome.ok ? undefined : "form-error"}
              >
                <strong>{outcome.label}</strong> — {outcome.detail}
              </li>
            ))}
          </ul>
          <div className="form-actions">
            <Button onClick={onClose}>{t("inventory.common.close")}</Button>
          </div>
        </>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <p className="inv-hint" data-testid="reserve-kind-statement">
            {t("inventory.reserve.quoteHoldStatement")}
          </p>

          <Field label={t("inventory.reserve.demandRef")}>
            <input
              // TESTID IS HISTORICAL (see the header): the field asked for an
              // id and now asks for a reference. Renaming it would break the
              // §10 walkthroughs that drive this panel for no user-visible gain.
              data-testid="reserve-demand-id"
              className="ds-input"
              value={demandRef}
              onChange={(event) => {
                setDemandRef(event.target.value);
              }}
              placeholder={t("inventory.reserve.demandRefPlaceholder")}
              maxLength={200}
              required
            />
          </Field>
          <p className="inv-hint">{t("inventory.reserve.demandRefHint")}</p>
          {refTooShort ? (
            <p className="field-error" data-testid="reserve-ref-too-short">
              {t("inventory.reserve.demandRefTooShort", { min: REF_MIN })}
            </p>
          ) : null}

          {multi ? (
            <p className="inv-hint">
              {t("inventory.reserve.multiHint", {
                n: list.length,
                total: formatMeasure(sumMeasures(list.map((item) => item.quantity)), {
                  locale: i18n.language,
                }),
                uom: single?.uom ?? "",
              })}
            </p>
          ) : (
            <Field label={t("inventory.reserve.quantity", { uom: single?.uom ?? "" })}>
              <input
                data-testid="reserve-quantity"
                className="ds-input"
                value={quantity}
                onChange={(event) => {
                  setQuantity(event.target.value);
                }}
                inputMode="decimal"
                required
              />
            </Field>
          )}

          <CheckboxField
            label={t("inventory.reserve.allowBackorder")}
            checked={allowBackorder}
            onChange={setAllowBackorder}
          />

          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              data-testid="reserve-submit"
              busy={busy}
              busyLabel={t("inventory.reserve.saving")}
              disabled={!refReady}
            >
              {t("inventory.reserve.submit")}
            </Button>
            <Button onClick={onClose}>{t("inventory.common.cancel")}</Button>
          </div>

          {/* D-118: the three kinds this panel does NOT place, named with the
              reason — an empty select or a silent 422 teaches nothing. */}
          <div className="inv-hint" data-testid="reserve-other-kinds">
            <p>{t("inventory.reserve.otherKindsHead")}</p>
            <ul className="inv-list">
              {OTHER_KINDS.map((kind) => (
                <li key={kind}>
                  <strong>{t(`inventory.reserve.kind.${kind}`)}</strong> —{" "}
                  {t(`inventory.reserve.otherKinds.${kind}`)}
                </li>
              ))}
            </ul>
          </div>
        </form>
      )}
    </section>
  );
}

/**
 * The combined length of the pieces, summed as scaled 1e-6 integers and handed
 * back as exact decimal text (D-042 — never a float sum of two ledger measures,
 * even for a hint line).
 */
function sumMeasures(values: readonly string[]): string {
  let total = 0n;
  for (const value of values) total += parseMeasure(value) ?? 0n;
  const negative = total < 0n;
  const abs = negative ? -total : total;
  const whole = abs / 1_000_000n;
  const frac = (abs % 1_000_000n).toString().padStart(6, "0");
  return `${negative ? "-" : ""}${whole.toString()}.${frac}`;
}
